import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import * as actiontracker from 'app/interfaces/actiontracker.interface';
import { IChatUser } from 'app/interfaces/chatuser.interface';
import { ApiError, ApiErrorResponse } from 'app/interfaces/error.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { ChatService } from 'app/services/chat.service';
import { ErrorService } from 'app/services/error.service';
import { EventsService } from 'app/services/events.service';
import { ZkuService } from 'app/services/zku.service';
import { slideDownAnimation } from 'app/utils/animations.util';
import { environment } from 'environments/environment';
import { IAppointment } from 'modules/video-conference/interfaces/appointment.interface';

@Component({
	selector: 'vc-page',
	templateUrl: './vc-page.component.html',
	styleUrls: ['./vc-page.component.scss'],
	animations: [slideDownAnimation],
})
export class VideoConferencePage {
	loading = true;
	appointment: IAppointment;
	displayState: 'switch' | 'chat' | 'sidebar' = 'sidebar';

	constructor(
		private actionTrackerService: ActionTrackerService,
		private activatedRoute: ActivatedRoute,
		private chatService: ChatService,
		private errorService: ErrorService,
		private eventsService: EventsService,
		private notificationService: ZkNotificationService,
		private router: Router,
		private translateService: TranslateService,
		private zkuService: ZkuService
	) {
		this.activatedRoute.params.subscribe((params) => {
			const hasAppointmentId = params['appointmentId'];

			if (hasAppointmentId) {
				return this.validateEventStart(params['appointmentId']);
			}

			this.router.navigate(['calendar']);
		});

		this.chatService.switchState.subscribe({
			next: (state: 'switch' | 'sidebar') => (this.displayState = state),
		});
	}

	private validateEventStart(appointmentId: string) {
		this.eventsService.validateEventStart(appointmentId).subscribe(
			(eventValidation) => {
				const isValidEvent = eventValidation.result;

				if (isValidEvent) {
					return this.setEventData(
						appointmentId,
						eventValidation.jitsiDomain || environment.jitsi.domain
					);
				}

				this.router.navigate(['calendar']);
			},
			() => {
				this.notificationService.showError(
					this.translateService.instant('SESSION.JITSI.SESSION_ERROR')
				);

				this.router.navigate(['calendar']);
			}
		);
	}

	private setEventData(appointmentId: string, jitsiDomain: string) {
		const professional = this.zkuService.getZku();

		this.eventsService.getEvent(appointmentId).then(
			(event) => {
				this.appointment = {
					id: appointmentId,
					serviceDomain: jitsiDomain,
					sessionModel: event?.subscription?.sessionModel,
					title:
						this.translateService.instant('PAGES.SESSION.VC_TITLE', {
							contact: event.userName,
							professional: professional.name,
						}) || appointmentId,
					contact: {
						contactId: event.contactId,
						id: event.profileId,
						name: event.userName,
					},
					professional: {
						avatarUrl: professional.imageUrl || '',
						id: professional.id,
						name: professional.name,
					},
					endTime: event.endTime,
					price: event.originalPrice,
					timezone: event.timeZone?.name || null,
				};

				this.loading = false;
			},
			(err: ApiErrorResponse) => {
				this.notificationService.showError(
					this.errorService.getMessage(err.error as ApiError)
				);
			}
		);
	}

	async openChat() {
		const chatUser: IChatUser = {
			id: this.appointment.contact.id,
			username: this.appointment.contact.name,
			role: this.chatService.role,
		};

		this.actionTrackerService.eventTracker(
			actiontracker.SCOPE_TYPE.Session,
			actiontracker.SUBSCOPE_TYPE.Conference,
			actiontracker.ITEM_TYPE.Professional,
			actiontracker.INTERACTION_TYPE.Chat,
			{ sessionId: this.appointment.id }
		);

		return await this.chatService.startConversation(
			chatUser,
			'video-conference',
			'',
			'videoConference'
		);
	}

	displayStateChange(state: 'switch' | 'chat' | 'sidebar') {
		this.displayState = state;

		if (state === 'chat') {
			this.openChat();
		}
	}
}
